import * as React from "react";
import { LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandSwitcher } from "@/components/chrome/BrandSwitcher";
import { useAuth } from "@/auth/useAuth";
import { useAppSelector } from "@/store";
import { selectProfile } from "@/store/selectors";

function computeInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return "BH";
  const initials = parts.slice(0, 2).map((w) => w[0]).join("");
  return initials.toUpperCase();
}

export function AdminChrome() {
  const profile = useAppSelector(selectProfile);
  const { logout } = useAuth();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const agencyName = profile.agencyName ?? "";
  const initials = computeInitials(agencyName);

  return (
    <header className="relative z-30 flex items-center justify-between px-6 py-5">
      <BrandSwitcher agencyName={agencyName} />
      <nav className="flex items-center gap-2">
        <div ref={ref} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className={cn(
              "grid h-8 w-8 place-items-center rounded-full text-[10px] font-medium uppercase tracking-wider transition-colors",
              menuOpen
                ? "bg-foreground text-background"
                : "bg-foreground/[0.04] text-foreground/70 ring-1 ring-foreground/[0.05] hover:bg-foreground/[0.07]",
            )}
            aria-label="Profilo"
          >
            {initials}
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 overflow-hidden rounded-[14px] bg-white/95 backdrop-blur-2xl shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_18px_50px_-14px_rgba(15,15,15,0.18)] animate-riseIn">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-foreground/60 transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
                Esci
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
